import { useEffect, useState } from 'react';
import {
  EventBus, useGameStore, useJwt, useAuthHeaders,
  FEEDBACK_MESSAGE_MIN, FEEDBACK_MESSAGE_MAX, type FeedbackKind, type FeedbackContext,
} from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { Button, Modal, Switch, Tabs, Textarea } from '../ds/index.js';
import { markFeedbackSent } from '../../lib/feedbackNudge.js';
import { sessionInfo } from '../people/membersStore.js';

const ERRORS: Record<string, string> = {
  TOO_MANY_FEEDBACK: 'Tu as déjà envoyé plusieurs retours cette heure-ci. Réessaie un peu plus tard.',
  INVALID_MESSAGE: `Décris en quelques mots (${FEEDBACK_MESSAGE_MIN} caractères au moins).`,
  INVALID_SCREENSHOT: 'La capture n’a pas pu être jointe. Décoche-la et réessaie.',
};

/** Contexte joint automatiquement — rien de privé : ni e-mail, ni jeton, ni paramètres d'URL. */
function browserContext(capture: { map?: string; tileX?: number; tileY?: number } | null): FeedbackContext {
  const s = useGameStore.getState();
  return {
    ...(capture?.map ? { map: capture.map } : {}),
    ...(s.currentZoneName ? { mapName: s.currentZoneName } : {}),
    ...(capture?.tileX !== undefined ? { tileX: capture.tileX } : {}),
    ...(capture?.tileY !== undefined ? { tileY: capture.tileY } : {}),
    ...(s.localBubbleId ? { bubble: s.localBubbleId } : {}),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screen: `${window.screen.width}×${window.screen.height} @${window.devicePixelRatio}x`,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    ...(import.meta.env.VITE_APP_VERSION ? { appVersion: String(import.meta.env.VITE_APP_VERSION).slice(0, 12) } : {}),
    url: `${window.location.origin}${window.location.pathname}`,
  };
}

/**
 * FeedbackModal — « Signaler un problème ou proposer une idée » (menu de
 * l'avatar). Capture du campus prise à l'ouverture (canvas seul, voir
 * ZoneScene `ui:feedback-capture`), jointe par défaut, retirable.
 */
export function FeedbackModal({ onClose, initialKind = 'bug' }: { onClose: () => void; initialKind?: FeedbackKind }) {
  const jwt = useJwt();
  const headers = useAuthHeaders(getTenantSlug());
  const displayName = useGameStore((s) => s.displayName);
  const [kind, setKind] = useState<FeedbackKind>(initialKind);
  const [message, setMessage] = useState('');
  const [capture, setCapture] = useState<{ dataUrl: string | null; map?: string; tileX?: number; tileY?: number } | null>(null);
  const [attach, setAttach] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Clavier rendu à la fenêtre (sinon ZQSD déplace l'avatar pendant la saisie) + capture à l'ouverture.
  useEffect(() => {
    EventBus.emit('ui:modal-opened', {});
    let done = false;
    EventBus.emit('ui:feedback-capture', { reply: (r) => { if (!done) setCapture(r); } });
    // Pas de scène pour répondre (chargement) : pas de capture.
    const timer = setTimeout(() => setCapture((c) => c ?? { dataUrl: null }), 1500);
    return () => { done = true; clearTimeout(timer); EventBus.emit('ui:modal-closed', {}); };
  }, []);

  const length = message.trim().length;
  const tooShort = length < FEEDBACK_MESSAGE_MIN;

  async function send() {
    if (tooShort || sending || !jwt) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind,
          message: message.trim(),
          context: browserContext(capture),
          ...(attach && capture?.dataUrl ? { screenshot: capture.dataUrl } : {}),
          authorName: displayName ?? undefined,
        }),
      });
      if (!res.ok) {
        const { error: code } = await res.json().catch(() => ({ error: '' })) as { error?: string };
        setError(ERRORS[code ?? ''] ?? `Ton retour n’a pas pu être envoyé (HTTP ${res.status}). Réessaie dans un instant.`);
        return;
      }
      const { userId, isGuest } = sessionInfo();
      if (userId && !isGuest) markFeedbackSent(userId);
      setSent(true);
    } catch {
      setError('Erreur réseau : ton retour n’a pas été envoyé. Réessaie dans un instant.');
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <Modal title="Merci !" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Fermer</Button>}>
        <p style={{ margin: 0, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
          {kind === 'bug'
            ? 'Ton signalement est bien arrivé. L’équipe va regarder ça.'
            : 'Ton idée est bien arrivée. Merci de nous aider à améliorer le campus.'}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Signaler un problème ou proposer une idée"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>Annuler</Button>
          <Button variant="primary" onClick={() => { void send(); }} disabled={tooShort || sending}>
            {sending ? 'Envoi…' : 'Envoyer'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => { e.preventDefault(); void send(); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--color-text-primary)' }}
      >
        <div>
          <Tabs aria-label="Type de retour" items={[{ id: 'bug', label: 'Un problème' }, { id: 'idea', label: 'Une idée' }]} value={kind} onChange={setKind} />
        </div>

        <Textarea
          label={kind === 'bug' ? 'Que s’est-il passé ?' : 'Quelle est ton idée ?'}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={FEEDBACK_MESSAGE_MAX}
          placeholder={kind === 'bug'
            ? 'Ce que tu faisais, ce qui s’est passé, ce que tu attendais…'
            : 'Ce qui te manque, ce qui te simplifierait la vie…'}
          hint={`${length} / ${FEEDBACK_MESSAGE_MAX}`}
          autoFocus
          {...(error ? { error } : {})}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {capture === null && <p className="pl-help" style={{ margin: 0 }}>Capture du campus…</p>}
          {capture?.dataUrl && (
            <>
              <Switch checked={attach} onChange={setAttach} label="Joindre une capture du campus" />
              {attach && (
                <img
                  src={capture.dataUrl}
                  alt="Capture du campus qui sera jointe"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)', background: 'var(--color-bg-raised)' }}
                />
              )}
            </>
          )}
          <p className="pl-help" style={{ margin: 0 }}>
            Sont joints automatiquement : la carte et ta position, ton navigateur et la taille de ton écran.
            La capture montre la carte seulement, sans les vidéos ni les fenêtres ouvertes.
          </p>
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
